import React from 'react';
import PageHeader from '../components/PageHeader';
import Callout from '../components/Callout';
import { motion } from 'framer-motion';

const tiers = [
  {
    horizon: 'This week',
    effort: 'Low effort, high return',
    items: [
      'Put one project under version control (git), even a solo one.',
      'Write a plain-text README that says what each file is and how to run the analysis.',
      'Adopt one consistent file-naming convention (e.g., YYYYMMDD_experiment_initials).',
      'Get an ORCID iD if you do not already have one.'
    ]
  },
  {
    horizon: 'This project',
    effort: 'Moderate effort, compounding return',
    items: [
      'Pre-register the hypotheses and analysis plan before you collect or unblind data.',
      'Record your computational environment (conda / renv / a container) so it can be rebuilt.',
      'Keep raw data read-only and separate from processed data and code.',
      'Draft the data availability statement early, not at submission.'
    ]
  },
  {
    horizon: 'This lab / long term',
    effort: 'Higher effort, cultural payoff',
    items: [
      'Move the workflow into a workflow manager so the pipeline is one executable object.',
      'Deposit data and code in a persistent repository with a DOI, under an explicit license.',
      'Make reproducibility part of onboarding and code review, not a solo heroic act.',
      'Adopt the reporting standard (EQUATOR) and TOP expectations of your target journals.'
    ]
  }
];

const resources = [
  {
    name: 'The Turing Way',
    url: 'https://book.the-turing-way.org/',
    desc: 'A comprehensive, community-written handbook for reproducible, ethical, and collaborative data science. The single best deep-dive reference.'
  },
  {
    name: 'The Carpentries',
    url: 'https://carpentries.org/',
    desc: 'Hands-on workshops teaching the foundational skills (shell, git, Python, R) that make reproducible analysis possible.'
  },
  {
    name: 'Reproducibility for Everyone',
    url: 'https://www.repro4everyone.org/',
    desc: 'Practical, discipline-agnostic training materials aimed at working researchers.'
  },
  {
    name: 'Center for Open Science',
    url: 'https://www.cos.io/',
    desc: 'Home of OSF, preregistration, Registered Reports, and the TOP Guidelines.'
  }
];

const GettingStarted = () => {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="prose-custom">
      <PageHeader
        badge="Start Here"
        title="Getting Started"
        subtitle="You do not have to do everything at once. Reproducibility is adopted in layers—here is a realistic order to add them."
      />

      <p>
        The fastest way to stall is to treat reproducibility as an all-or-nothing overhaul. It is not.
        Every item below stands on its own and pays off even if you never do the next one. Start with
        one thing this week.
      </p>

      <div className="space-y-6 my-10">
        {tiers.map((tier) => (
          <div key={tier.horizon} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
              <h3 className="text-xl text-brand-900 mt-0 mb-0">{tier.horizon}</h3>
              <span className="text-xs font-semibold uppercase tracking-wider text-brand-700 bg-brand-100 px-2.5 py-1 rounded-full">
                {tier.effort}
              </span>
            </div>
            <ul className="space-y-2 m-0">
              {tier.items.map((item, i) => (
                <li key={i} className="text-slate-700">{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <Callout type="info" title="A realistic word about incentives">
        <p className="mb-0">
          None of this is free of friction. Academic reward systems still emphasize novelty and
          publication volume over verification, and that tension is real—pretending otherwise helps
          no one. The honest case for adopting these practices is partly self-interested: reproducible
          projects are easier to revisit, hand off, and defend, and they save your future self from
          results you can no longer reconstruct. Start where the return to <em>you</em> is highest.
        </p>
      </Callout>

      <h2>Where to Go Deeper</h2>
      <p>
        This site is a map and an on-ramp, not an encyclopedia. When you are ready to go further on any
        stage, these established, openly-licensed resources are where the depth lives:
      </p>

      <div className="grid sm:grid-cols-2 gap-4 my-8">
        {resources.map((r) => (
          <a
            key={r.name}
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-white p-5 rounded-lg border border-slate-200 hover:border-brand-300 hover:shadow-md transition-all no-underline"
          >
            <h4 className="text-lg font-bold text-slate-800 mb-2 mt-0">{r.name}</h4>
            <p className="text-sm text-slate-600 m-0">{r.desc}</p>
          </a>
        ))}
      </div>
    </motion.div>
  );
};

export default GettingStarted;
